import type { App } from 'vue';
import PrimeVue from 'primevue/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

// SeraPreset extends Aura by mapping semantic.primary.* to the blue palette.
// #1e40af is blue.800 in Tailwind's scale — the placeholder primary for Phase 2.
// Phase 3 will replace this with dynamic per-tenant color (see RESEARCH.md Open Question 1).
const SeraPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

// Global pt object — default styling for all PrimeVue Button, InputText, Card
// instances. Base components in libs/ui add variant-specific overrides via their
// own pt props which merge with this global pt (ptOptions.mergeSections = true).
//
// CRITICAL: every class string here must be a fully-written class name. Tailwind's
// source scanner reads these as plain text — fragmented templates like
// `text-${color}-500` are NOT detected. Arbitrary value classes referencing CSS
// vars (bg-[var(--color-primary)]) are detected because the literal string is
// visible.
const globalPt = {
  button: {
    // Default button styling — neutral; base components override per variant.
    // Touch target min-height 44px on mobile (UI-SPEC BaseButton).
    root: 'inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-normal min-h-[44px] sm:min-h-[40px] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed',
    label: 'flex-1 text-center',
    icon: 'mr-2',
    loadingIcon: 'animate-spin mr-2 h-4 w-4',
  },
  inputtext: {
    // PrimeVue InputText is a single <input> — the wrapper, label, and error
    // message are authored by libs/ui/src/components/BaseInput.vue directly.
    // UI-SPEC BaseInput default state row dictates these classes.
    root: 'h-10 w-full px-3 py-2 text-base bg-field border border-border-strong rounded-md text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed',
  },
  card: {
    // UI-SPEC BaseCard table — bg-surface, border, rounded-lg, shadow-sm, p-6
    // (default padding lives on body to avoid double-padding when header slot
    // adds its own padding).
    root: 'bg-surface border border-border rounded-lg shadow-sm',
    body: 'p-6',
    title: 'text-xl font-semibold text-foreground mb-2',
    subtitle: 'text-sm text-muted mb-4',
    content: 'text-base text-foreground',
    footer: 'px-6 py-4 border-t border-border',
  },
  toast: {
    // PrimeVue 4 Toast unstyled — positioned top-right via root classes.
    // Severity-specific left border colors are applied via CSS data-attribute
    // selectors in main.css (data-p-severity="warn" etc.).
    root: 'fixed top-4 right-4 z-50 w-80',
    message:
      'mb-2 rounded-lg border shadow-lg p-4 flex items-start gap-3 bg-surface border-border text-foreground',
    messageContent: 'flex items-start gap-3 w-full',
    messageIcon: 'shrink-0 text-lg mt-0.5',
    messageText: 'flex-1 min-w-0',
    summary: 'font-semibold text-sm text-foreground',
    detail: 'text-sm text-muted mt-0.5',
    closeButton:
      'shrink-0 ml-auto p-1 rounded hover:bg-surface-hover transition-colors text-muted hover:text-foreground',
    closeIcon: 'size-3.5',
  },
};

export function installPrimeVue(app: App): void {
  app.use(PrimeVue, {
    unstyled: true,
    pt: globalPt,
    ptOptions: {
      mergeSections: true,
      mergeProps: false,
    },
    theme: {
      preset: SeraPreset,
      options: { darkModeSelector: false },
    },
  });
}
